import { Injectable } from '@angular/core';
import {
  Firestore,
  FirestoreError,
  Unsubscribe,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { FirebaseAppService } from '../../../core/services/firebase-app.service';
import { ChatMessage, MessageReplyTo } from '../models/message.model';
import { Thread, ThreadParticipantInfo } from '../models/thread.model';

@Injectable({
  providedIn: 'root'
})
export class MessagingService {
  private firestore: Firestore;

  constructor(private firebaseAppService: FirebaseAppService) {
    this.firestore = this.firebaseAppService.getFirestore();
  }

  threadIdFor(uidA: string, uidB: string): string {
    return [uidA, uidB].sort().join('__');
  }

  listThreads(myUid: string, callback: (threads: Thread[]) => void): Unsubscribe {
    const threadsQuery = query(
      collection(this.firestore, 'dm_threads'),
      where('participants', 'array-contains', myUid),
      orderBy('updatedAt', 'desc')
    );

    return onSnapshot(
      threadsQuery,
      (snapshot) => {
        callback(snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() } as Thread)));
      },
      (error: FirestoreError) => {
        console.error('listThreads snapshot error', error);
      }
    );
  }

  listMessages(threadId: string, callback: (messages: ChatMessage[]) => void): Unsubscribe {
    const messagesQuery = query(
      collection(this.firestore, `dm_threads/${threadId}/messages`),
      orderBy('sentAt', 'asc')
    );

    return onSnapshot(
      messagesQuery,
      (snapshot) => {
        callback(snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() } as ChatMessage)));
      },
      (error: FirestoreError) => {
        console.error('listMessages snapshot error', error);
      }
    );
  }

  async getOrCreateThread(
    myUid: string,
    myInfo: ThreadParticipantInfo,
    otherUid: string,
    otherInfo: ThreadParticipantInfo
  ): Promise<string> {
    const threadId = this.threadIdFor(myUid, otherUid);
    const threadRef = doc(this.firestore, 'dm_threads', threadId);
    const existing = await getDoc(threadRef);

    if (!existing.exists()) {
      await setDoc(threadRef, {
        participants: [myUid, otherUid],
        participantsInfo: {
          [myUid]: myInfo,
          [otherUid]: otherInfo
        },
        lastMessage: null,
        unreadCount: { [myUid]: 0, [otherUid]: 0 },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }

    return threadId;
  }

  async sendMessage(
    threadId: string,
    senderUid: string,
    recipientUid: string,
    text: string,
    replyTo?: MessageReplyTo | null
  ): Promise<void> {
    const messagesRef = collection(this.firestore, `dm_threads/${threadId}/messages`);
    await addDoc(messagesRef, {
      senderId: senderUid,
      text,
      sentAt: serverTimestamp(),
      readBy: [senderUid],
      replyTo: replyTo ?? null
    });

    const threadRef = doc(this.firestore, 'dm_threads', threadId);
    await updateDoc(threadRef, {
      lastMessage: { text, senderId: senderUid, sentAt: serverTimestamp() },
      updatedAt: serverTimestamp(),
      [`unreadCount.${recipientUid}`]: increment(1)
    });
  }

  /**
   * Edits a message in place (same doc id, untouched sentAt) rather than
   * sending a new one - this is what keeps it a single row in the Message
   * Log instead of appearing as a second send, since that log is read live
   * off these same Firestore docs rather than a separate persisted table.
   */
  async editMessage(threadId: string, messageId: string, text: string): Promise<void> {
    await updateDoc(doc(this.firestore, `dm_threads/${threadId}/messages`, messageId), {
      text,
      editedAt: serverTimestamp()
    });

    await this.refreshLastMessage(threadId);
  }

  async markThreadRead(threadId: string, myUid: string): Promise<void> {
    const threadRef = doc(this.firestore, 'dm_threads', threadId);
    await updateDoc(threadRef, {
      [`unreadCount.${myUid}`]: 0
    });
  }

  /**
   * Deletes a message and re-derives the thread's denormalized `lastMessage`
   * from what's left, so the inbox preview doesn't keep showing text for a
   * message that no longer exists.
   */
  async deleteMessage(threadId: string, messageId: string): Promise<void> {
    await deleteDoc(doc(this.firestore, `dm_threads/${threadId}/messages`, messageId));
    await this.refreshLastMessage(threadId);
  }

  private async refreshLastMessage(threadId: string): Promise<void> {
    const latestQuery = query(
      collection(this.firestore, `dm_threads/${threadId}/messages`),
      orderBy('sentAt', 'desc'),
      limit(1)
    );
    const latestSnapshot = await getDocs(latestQuery);
    const latest = latestSnapshot.docs[0]?.data() as ChatMessage | undefined;

    const threadRef = doc(this.firestore, 'dm_threads', threadId);
    await updateDoc(threadRef, {
      lastMessage: latest ? { text: latest.text, senderId: latest.senderId, sentAt: latest.sentAt } : null
    });
  }
}
